import React, { useState, useEffect } from 'react';
import { makeRequest } from '../../../utils/makeRequest';
import { vendorRequestPageURL, vendorApproveRequestURL, vendorDenyRequestURL } from '../../../utils/urls';
import { toastify } from '../../../utils/toast';

const VendorApproval = () => {
    const [requestData, setRequestData] = useState(null);
    const [quote, setQuote] = useState('');
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSubmitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(null);

    // Extract token from URL query params
    const token = new URLSearchParams(window.location.search).get('token');

    // Fetch service request data on mount
    useEffect(() => {
        if (!token) {
            setError('Invalid or missing link. Please use the link sent to you via SMS or email.');
            setPageLoading(false);
            return;
        }

        const fetchRequestData = async () => {
            try {
                const response = await makeRequest(`${vendorRequestPageURL}?token=${token}`);
                console.log("respose", response);

                if (response.success) {
                    setRequestData(response.data);
                }
            } catch (err) {
                setError('Network error. Please check your connection and try again.');
            } finally {
                setPageLoading(false);
            }
        };

        fetchRequestData();
    }, [token]);

    const handleApprove = async () => {
        setSubmitting(true);
        setLoading(true);
        try {
            const response = await makeRequest(`${vendorApproveRequestURL}`, 'POST', { token, quote, notes });
            console.log("respose", response);

            if (response.success) {
                setSubmitted('approved');
                alert("Request approved successfully!");
                toastify("Request accepted successfully!", 'success');
            } else {
                toastify(response.message, 'error');
            }
        } catch (err) {
            toastify(err.message, 'error');
        } finally {
            setSubmitting(false);
            setLoading(false);
        }
    };

    const handleReject = async () => {
        setLoading(true);

        try {
            const response = await makeRequest(`${vendorDenyRequestURL}`, 'POST', { token, notes });
            console.log("respose", response);

            if (response.success) {
                setSubmitted('rejected');
            } else {
                toastify(response.data.message, 'error');
            }
        } catch (err) {
            toastify(err.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    // ─── Loading State ───────────────────────────────────────────────────────
    if (pageLoading) {
        return (
            <div className="container py-5 text-center">
                <div className="spinner-border text-primary" role="status" />
                <p className="mt-3 text-muted">Loading your service request...</p>
            </div>
        );
    }

    // ─── Error State ─────────────────────────────────────────────────────────
    if (error) {
        return (
            <div className="container py-5">
                <div className="alert alert-danger text-center">
                    <i className="ti ti-alert-circle fs-2 mb-2 d-block" />
                    <strong>Unable to load request</strong>
                    <p className="mb-0 mt-1">{error}</p>
                </div>
            </div>
        );
    }

    // ─── Success State ────────────────────────────────────────────────────────
    if (submitted) {
        return (
            <div className="container py-5">
                <div className={`alert ${submitted === 'approved' ? 'alert-success' : 'alert-warning'} text-center`}>
                    <i className={`ti ${submitted === 'approved' ? 'ti-circle-check' : 'ti-circle-x'} fs-2 mb-2 d-block`} />
                    <strong>
                        {submitted === 'approved'
                            ? 'Quote Submitted Successfully!'
                            : 'Request Rejected'}
                    </strong>
                    <p className="mb-0 mt-1">
                        {submitted === 'approved'
                            ? `Your quotation of KES ${Number(quote).toLocaleString()} has been submitted. The resident has been notified`
                            : 'You have rejected this service request. The facility admin has been notified.'}
                    </p>
                </div>
            </div>
        );
    }

    // ─── Main View ────────────────────────────────────────────────────────────
    const { serviceRequest, vendor } = requestData;

    const formattedDate = serviceRequest.date
        ? new Date(serviceRequest.date).toLocaleDateString('en-KE', {
            day: 'numeric', month: 'long', year: 'numeric'
        })
        : '—';

    return (
        <div className="container py-4" style={{ maxWidth: 600 }}>
            <div className="card shadow-sm border-0">

                {/* Header */}
                <div className="card-header">
                    <h5 className="mb-0">Service Request Approval</h5>
                    <small className="opacity-75">Assigned to: {vendor.name}</small>
                </div>

                <div className="card-body">

                    {/* Request Info */}
                    <div className="mb-4">
                        <h6 className="text-muted">Request Details for:</h6>
                        <h6>{serviceRequest.service.serviceName}</h6>
                        <div className="bg-light p-3 rounded">
                            <p className="mb-2">
                                <strong>Unit:</strong> {serviceRequest.unit?.name}
                            </p>
                            <p className="mb-2">
                                <strong>Request No:</strong> {serviceRequest.requestNumber}
                            </p>
                            <p className="mb-2">
                                <strong>Description:</strong> {serviceRequest.description}
                            </p>
                            <p className="mb-2">
                                <strong>Preferred Date:</strong> {formattedDate}
                            </p>
                            <p className="mb-2">
                                <strong>Preferred Time:</strong> {serviceRequest.time || '—'}
                            </p>
                            <p className="mb-0">
                                <strong>Status:</strong>
                                <span className="badge bg-warning ms-2">
                                    {serviceRequest.status}
                                </span>
                            </p>
                        </div>
                    </div>

                    <div className="mb-4">
                        <h6 className="text-muted">Customer Info:</h6>
                        <div className="bg-light p-3 rounded">
                            <p className="mb-2">
                                <strong>Name: </strong> {serviceRequest.customer.fullName}
                            </p>
                            <p className="mb-2">
                                <strong>Phone: </strong> {serviceRequest.customer.phoneNumber}
                            </p>
                            <p className="mb-2">
                                <strong>Email: </strong> {serviceRequest.customer.email}
                            </p>
                        </div>
                    </div>

                    {/* Quotation Section */}
                    <div className="mb-4">
                        <h6 className="text-muted">Your Quotation</h6>

                        <div className="mb-3">
                            <label className="form-label">
                                Quote (KES) <span className="text-danger">*</span>
                            </label>
                            <input
                                type="number"
                                className="form-control form-control-lg"
                                placeholder="Enter quote"
                                value={quote}
                                min="1"
                                onChange={(e) => setQuote(e.target.value)}
                                disabled={loading}
                            />
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Notes (optional)</label>
                            <textarea
                                className="form-control"
                                rows="3"
                                placeholder="Add any notes or conditions..."
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                disabled={loading}
                            />
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="d-flex gap-3">
                        <button
                            className="btn btn-success btn-lg w-100"
                            onClick={handleApprove}
                            disabled={loading}
                        >
                            {loading ? (
                                <span className="spinner-border spinner-border-sm me-2" />
                            ) : (
                                <i className="ti ti-check me-2" />
                            )}
                            {isSubmitting ? 'Submitting...' : 'Approve & Submit Quote'}
                        </button>

                        <button
                            className="btn btn-outline-danger btn-lg w-100"
                            onClick={handleReject}
                            disabled={loading}
                        >
                            {loading ? (
                                <span className="spinner-border spinner-border-sm me-2" />
                            ) : (
                                <i className="ti ti-x me-2" />
                            )}
                            Reject Request
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default VendorApproval;